import React, { useState } from 'react';
import styled from 'styled-components';

interface InputProps {
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
  secondary?: boolean;
  min?: string; // Add min prop for number inputs
  max?: string; // Add max prop for number inputs
  step?: string; // Add step prop for number inputs
}

const InputContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 14px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ readOnly?: boolean; secondary?: boolean; isPassword?: boolean }>`
  width: 100%;
  padding: 14px;
  padding-right: ${props => (props.readOnly || props.isPassword) ? '40px' : '14px'};
  font-size: 16px;
  border: none;
  border-radius: 12px;
  background: ${props => props.secondary ? '#f5f5f5' : 'white'};
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: ${props => props.readOnly ? '#666' : '#333'};
`;

const LockIcon = styled.div`
  position: absolute;
  right: 14px;
  color: #666;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  height: 18px;
  width: 18px;
  
  &:hover {
    color: #333;
  }
  
  &:focus {
    outline: none;
    color: #333;
  }
`;

const ErrorText = styled.p`
  color: #e53935;
  margin-top: 6px;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
`;

const Input: React.FC<InputProps> = ({
  type,
  value,
  onChange,
  placeholder,
  name,
  label,
  required,
  error,
  readOnly,
  secondary,
  min,
  max,
  step
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <InputContainer>
      <InputLabel htmlFor={name}>
        {label} {required && <span style={{ color: '#e53935' }}>*</span>}
      </InputLabel>
      <InputWrapper>
        <StyledInput
          type={inputType}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          secondary={secondary}
          isPassword={isPasswordField}
          min={min}
          max={max}
          step={step}
        />
        {readOnly && (
          <LockIcon>
            <i className='bx bxs-lock'></i>
          </LockIcon>
        )}
        {isPasswordField && !readOnly && (
          <PasswordToggle
            type="button"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <i className={showPassword ? 'bx bx-hide' : 'bx bx-show'}></i>
          </PasswordToggle>
        )}
      </InputWrapper>
      {error && <ErrorText>{error}</ErrorText>}
    </InputContainer>
  );
};

export default Input;